import React, {useMemo} from 'react';
import {RowWrapper, BaselineRowWrapper, GrowableColumn} from './Elements.styled';
import {keyBy} from 'lodash';
import {Column} from './Elements.styled';
import {getColumnWidth} from './utils';
import {useIntl} from 'react-intl';
import Util from '../../../../util/util';
import {NumericTileV2, UNIT} from './task-row-elements/NumericTile';

export const BaselineRow = ({
	node: {baselineTargetMinutes, baselineTargetPrice, baselineCost, availableColumns, currencySymbol, currency},
}) => {
	const intl = useIntl();
	const columnsObject = useMemo(() => keyBy(availableColumns, 'name'), [availableColumns]);

	const nameColumnWidth = useMemo(() => {
		let width = getColumnWidth('task-name', columnsObject);
		const idColumnWidth = getColumnWidth('task-id', columnsObject);
		width += idColumnWidth || 0;
		return width;
	}, [availableColumns]);

	return (
		<RowWrapper>
			<BaselineRowWrapper>
				{/* task-id and task-name used for the same column*/}
				<GrowableColumn width={nameColumnWidth}>{intl.formatMessage({id: 'baseline.baseline'})}</GrowableColumn>
				{getColumnWidth('date', columnsObject) && <Column width={getColumnWidth('date', columnsObject)} />}
				{getColumnWidth('assigned-role', columnsObject) && (
					<Column width={getColumnWidth('assigned-role', columnsObject)} />
				)}
				{getColumnWidth('assigned-person', columnsObject) && (
					<Column width={getColumnWidth('assigned-person', columnsObject)} />
				)}
				{getColumnWidth('status', columnsObject) && <Column width={getColumnWidth('status', columnsObject)} />}
				{getColumnWidth('phase', columnsObject) && <Column width={getColumnWidth('phase', columnsObject)} />}
				{/* The three client-workspace columns carry values only on phase-header rows
				    (sign-off status) and task rows (the per-task sign-off pair), so this row draws
				    same-width empty cells to keep every column to its right aligned. */}
				{getColumnWidth('phase-signoff-status', columnsObject) && (
					<Column width={getColumnWidth('phase-signoff-status', columnsObject)} />
				)}
				{getColumnWidth('portal-visible', columnsObject) && (
					<Column width={getColumnWidth('portal-visible', columnsObject)} />
				)}
				{getColumnWidth('portal-signoff', columnsObject) && (
					<Column width={getColumnWidth('portal-signoff', columnsObject)} />
				)}
				{getColumnWidth('portal-approver', columnsObject) && (
					<Column width={getColumnWidth('portal-approver', columnsObject)} />
				)}
				{getColumnWidth('deliverable', columnsObject) && (
					<Column width={getColumnWidth('deliverable', columnsObject)} />
				)}
				{getColumnWidth('sprint', columnsObject) && <Column width={getColumnWidth('sprint', columnsObject)} />}
				{getColumnWidth('done-percentage', columnsObject) && (
					<Column width={getColumnWidth('done-percentage', columnsObject)} />
				)}
				{getColumnWidth('period-target', columnsObject) && (
					<Column width={getColumnWidth('period-target', columnsObject)} />
				)}
				{getColumnWidth('forecast', columnsObject) && (
					<BaselineEstimateColumn
						baselineTargetMinutes={baselineTargetMinutes}
						width={getColumnWidth('forecast', columnsObject)}
					/>
				)}
				{getColumnWidth('time-entries', columnsObject) && (
					<Column width={getColumnWidth('time-entries', columnsObject)} />
				)}
				{getColumnWidth('billable-time-entries', columnsObject) && (
					<Column width={getColumnWidth('billable-time-entries', columnsObject)} />
				)}
				{getColumnWidth('non-billable-time-entries', columnsObject) && (
					<Column width={getColumnWidth('non-billable-time-entries', columnsObject)} />
				)}
				{getColumnWidth('remaining', columnsObject) && <Column width={getColumnWidth('remaining', columnsObject)} />}
				{getColumnWidth('over-forecast', columnsObject) && (
					<Column width={getColumnWidth('over-forecast', columnsObject)} />
				)}
				{getColumnWidth('actual-price', columnsObject) && (
					<Column width={getColumnWidth('actual-price', columnsObject)} />
				)}
				{/* Projected Billable Value of Service */}
				{getColumnWidth('projected-billable-value-of-service', columnsObject) && (
					<BaselineProjectedBillableValueOfServiceColumn
						baselineTargetPrice={baselineTargetPrice}
						currencySymbol={currencySymbol}
						currencyCode={currency}
						width={getColumnWidth('projected-billable-value-of-service', columnsObject)}
						formatNumber={intl.formatNumber}
					/>
				)}
				{getColumnWidth('actual-cost', columnsObject) && (
					<Column width={getColumnWidth('actual-cost', columnsObject)} />
				)}
				{getColumnWidth('projected-cost', columnsObject) && (
					<BaselineProjectedCostColumn
						baselineCost={baselineCost}
						currencySymbol={currencySymbol}
						currencyCode={currency}
						width={getColumnWidth('projected-cost', columnsObject)}
						formatNumber={intl.formatNumber}
					/>
				)}
				{getColumnWidth('labels', columnsObject) && <Column width={getColumnWidth('labels', columnsObject)} />}
				{getColumnWidth('approved', columnsObject) && <Column width={getColumnWidth('approved', columnsObject)} />}
				<Column width={60} />
				<Column width={20} />
			</BaselineRowWrapper>
		</RowWrapper>
	);
};

const BaselineEstimateColumn = React.memo(({baselineTargetMinutes, width}) => {
	return (
		<Column width={width}>
			<NumericTileV2
				key={'baseline-estimate'}
				value={baselineTargetMinutes}
				unit={UNIT.HOURS}
				cy={'baseline-estimate-tile'}
				inputIndent
			/>
		</Column>
	);
});

const BaselineProjectedBillableValueOfServiceColumn = React.memo(
	({baselineTargetPrice, currencySymbol, currencyCode, width, formatNumber}) => {
		const placeUnitBeforeValue = Util.CurrencyIsPrefixed(currencySymbol, currencyCode);

		const projectedBillableValueOfService = formatNumber(baselineTargetPrice, {
			format: 'always_two_decimal',
		});
		return (
			<Column width={width}>
				<NumericTileV2
					key={'baseline-projected-billable-value-of-service'}
					placeUnitBeforeValue={placeUnitBeforeValue}
					value={projectedBillableValueOfService}
					unit={currencySymbol}
					cy={'baseline-projected-billable-value-of-service-tile'}
				/>
			</Column>
		);
	}
);

const BaselineProjectedCostColumn = React.memo(({baselineCost, currencySymbol, currencyCode, width, formatNumber}) => {
	const placeUnitBeforeValue = Util.CurrencyIsPrefixed(currencySymbol, currencyCode);

	const costNumber = formatNumber(baselineCost, {format: 'always_two_decimal'});
	return (
		<Column width={width}>
			<NumericTileV2
				key={'baseline-projected-cost'}
				placeUnitBeforeValue={placeUnitBeforeValue}
				value={costNumber}
				unit={currencySymbol}
				cy={'baseline-projected-cost-tile'}
			/>
		</Column>
	);
});
